import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FavoritesRecord, RecentSearch, RecentSearchesRecord } from '../types/angelNumber';
const FAVORITES_KEY = 'angel-numbers:favorites';
const RECENTS_KEY = 'angel-numbers:recent-searches';
const LOCAL_USER = 'local-user';
const MAX_RECENTS = 10;
function readFavorites(): FavoritesRecord {
  try {
    const raw = window.localStorage.getItem(FAVORITES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<FavoritesRecord>;
      if (Array.isArray(parsed?.favorites)) {
        return {
          userId: parsed.userId ?? LOCAL_USER,
          favorites: parsed.favorites.filter((id): id is string => typeof id === 'string')
        };
      }
    }
  } catch {
    /* fall through to an empty record */
  }
  return {
    userId: LOCAL_USER,
    favorites: []
  };
}
function readRecents(): RecentSearchesRecord {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<RecentSearchesRecord>;
      if (Array.isArray(parsed?.recentSearches)) {
        return {
          recentSearches: parsed.recentSearches.filter((item): item is RecentSearch => typeof item?.numberId === 'string').map(item => ({
            numberId: item.numberId,
            searchedAt: item.searchedAt ?? Date.now()
          }))
        };
      }
    }
  } catch {
    /* fall through to an empty record */
  }
  return {
    recentSearches: []
  };
}
function persist(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — data stays in memory for this session */
  }
}
interface LibraryValue {
  favoritesRecord: FavoritesRecord;
  favorites: string[];
  recentSearches: RecentSearch[];
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
  removeFavorite: (id: string) => void;
  addRecentSearch: (numberId: string) => void;
  clearRecentSearches: () => void;
}
const LibraryContext = createContext<LibraryValue | undefined>(undefined);
export function LibraryProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [favoritesRecord, setFavoritesRecord] = useState<FavoritesRecord>(readFavorites);
  const [recentsRecord, setRecentsRecord] = useState<RecentSearchesRecord>(readRecents);
  useEffect(() => {
    persist(FAVORITES_KEY, favoritesRecord);
  }, [favoritesRecord]);
  useEffect(() => {
    persist(RECENTS_KEY, recentsRecord);
  }, [recentsRecord]);
  const favorites = favoritesRecord.favorites;
  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);
  const toggleFavorite = useCallback((id: string) => {
    setFavoritesRecord(prev => ({
      ...prev,
      favorites: prev.favorites.includes(id) ? prev.favorites.filter(favorite => favorite !== id) : [id, ...prev.favorites]
    }));
  }, []);
  const removeFavorite = useCallback((id: string) => {
    setFavoritesRecord(prev => ({
      ...prev,
      favorites: prev.favorites.filter(favorite => favorite !== id)
    }));
  }, []);
  const addRecentSearch = useCallback((numberId: string) => {
    setRecentsRecord(prev => ({
      recentSearches: [{
        numberId,
        searchedAt: Date.now()
      }, ...prev.recentSearches.filter(item => item.numberId !== numberId)].slice(0, MAX_RECENTS)
    }));
  }, []);
  const clearRecentSearches = useCallback(() => setRecentsRecord({
    recentSearches: []
  }), []);
  const value = useMemo(() => ({
    favoritesRecord,
    favorites,
    recentSearches: recentsRecord.recentSearches,
    isFavorite,
    toggleFavorite,
    removeFavorite,
    addRecentSearch,
    clearRecentSearches
  }), [favoritesRecord, favorites, recentsRecord.recentSearches, isFavorite, toggleFavorite, removeFavorite, addRecentSearch, clearRecentSearches]);
  return <LibraryContext.Provider value={value} data-id="element-93" data-mp="14apxwfvjjp38">{children}</LibraryContext.Provider>;
}
export function useLibrary(): LibraryValue {
  const ctx = useContext(LibraryContext);
  if (!ctx) throw new Error('useLibrary must be used inside a LibraryProvider');
  return ctx;
}